/**
 * Input range consumed by {@link validateMetricRanges}.
 *
 * @property minTagId Optional lower-bound tag id (`string | null | undefined`); when a range starts at
 * a shared boundary, it must match the previous range's `maxTagId` (for example, `tag-10` at `rangeMin: 10`).
 * @property maxTagId Optional upper-bound tag id (`string | null | undefined`); when a range ends at
 * a shared boundary, it must match the next range's `minTagId` (for example, `tag-10` at `rangeMax: 10`).
 */
export type MetricRangeInput = {
  id?: string | null;
  rangeMin: number | null;
  rangeMax: number | null;
  rangeMinInclusive: boolean;
  rangeMaxInclusive: boolean;
  notificationEnabled: boolean;
  ideal: boolean;
  name?: string | null;
  minTagId?: string | null;
  maxTagId?: string | null;
};

export enum MetricRangeValidationErrorCode {
  METRIC_RANGE_NAME_REQUIRED = "METRIC_RANGE_NAME_REQUIRED",
  METRIC_RANGE_INVERTED = "METRIC_RANGE_INVERTED",
  METRIC_RANGE_INVALID_SINGLETON = "METRIC_RANGE_INVALID_SINGLETON",
  METRIC_RANGE_SINGLETON_MULTIPLE_TAGS = "METRIC_RANGE_SINGLETON_MULTIPLE_TAGS",
  METRIC_RANGE_MULTIPLE_SINGLETON = "METRIC_RANGE_MULTIPLE_SINGLETON",
  METRIC_RANGE_OVERLAP = "METRIC_RANGE_OVERLAP",
  METRIC_RANGE_BOTH_INCLUSIVE = "METRIC_RANGE_BOTH_INCLUSIVE",
  METRIC_RANGE_NEITHER_INCLUSIVE = "METRIC_RANGE_NEITHER_INCLUSIVE",
  METRIC_RANGE_IDEAL_REQUIRED = "METRIC_RANGE_IDEAL_REQUIRED",
  METRIC_RANGE_MULTIPLE_IDEAL = "METRIC_RANGE_MULTIPLE_IDEAL",
  METRIC_RANGE_IDEAL_NOT_INCLUSIVE = "METRIC_RANGE_IDEAL_NOT_INCLUSIVE",
  METRIC_RANGE_GAP = "METRIC_RANGE_GAP",
  METRIC_RANGE_TAG_MISMATCH = "METRIC_RANGE_TAG_MISMATCH",
}

export type MetricRangeValidationErrorDetails = {
  range?: MetricRangeInput;
  rangeIndex?: number;
  adjacentRanges?: {
    currentRange: MetricRangeInput;
    currentRangeIndex: number;
    nextRange: MetricRangeInput;
    nextRangeIndex: number;
  };
  sortedRanges?: MetricRangeInput[];
};

export class MetricRangeValidationError extends Error {
  public readonly code: MetricRangeValidationErrorCode;

  public readonly details?: MetricRangeValidationErrorDetails;

  public constructor(
    code: MetricRangeValidationErrorCode,
    msg: string,
    details?: MetricRangeValidationErrorDetails,
  ) {
    super(msg);
    this.name = "MetricRangeValidationError";
    this.code = code;
    this.details = details;
  }
}

export type MetricRangeValidationResult = {
  success: boolean;
  error: MetricRangeValidationError | null;
};

function effectiveMin(range: MetricRangeInput): number {
  return range.rangeMin ?? -Infinity;
}

function effectiveMax(range: MetricRangeInput): number {
  return range.rangeMax ?? Infinity;
}

function validationFailure(
  code: MetricRangeValidationErrorCode,
  msg: string,
  details?: MetricRangeValidationErrorDetails,
): MetricRangeValidationResult {
  return {
    success: false,
    error: new MetricRangeValidationError(code, msg, details),
  };
}

function validateRangeNames(ranges: MetricRangeInput[]): MetricRangeValidationResult | null {
  const unnamedRangeIndex = ranges.findIndex((range) => !range.name || range.name.trim() === "");
  const unnamed = ranges[unnamedRangeIndex];
  if (unnamed) {
    return validationFailure(
      MetricRangeValidationErrorCode.METRIC_RANGE_NAME_REQUIRED,
      "Every metric range must have a name",
      { range: unnamed, rangeIndex: unnamedRangeIndex },
    );
  }

  return null;
}

function validateRangeOrder(ranges: MetricRangeInput[]): MetricRangeValidationResult | null {
  for (let rangeIndex = 0; rangeIndex < ranges.length; rangeIndex++) {
    const range = ranges[rangeIndex];
    if (range.rangeMin !== null && range.rangeMax !== null && range.rangeMin > range.rangeMax) {
      return validationFailure(
        MetricRangeValidationErrorCode.METRIC_RANGE_INVERTED,
        `Invalid range: min (${range.rangeMin}) is greater than max (${range.rangeMax})`,
        { range, rangeIndex },
      );
    }
  }

  return null;
}

function validateSingletonRanges(ranges: MetricRangeInput[]): MetricRangeValidationResult | null {
  const singletonRanges = ranges.filter(
    (range) => range.rangeMin !== null && range.rangeMin === range.rangeMax,
  );

  const invalidSingleton = singletonRanges.find(
    (range) => !range.rangeMinInclusive || !range.rangeMaxInclusive,
  );
  if (invalidSingleton) {
    const rangeIndex = ranges.indexOf(invalidSingleton);
    return validationFailure(
      MetricRangeValidationErrorCode.METRIC_RANGE_INVALID_SINGLETON,
      `Singleton range at ${invalidSingleton.rangeMin} must include both boundaries`,
      { range: invalidSingleton, rangeIndex },
    );
  }

  const singletonWithMultipleTags = singletonRanges.find((range) => range.minTagId && range.maxTagId);
  if (singletonWithMultipleTags) {
    const rangeIndex = ranges.indexOf(singletonWithMultipleTags);
    return validationFailure(
      MetricRangeValidationErrorCode.METRIC_RANGE_SINGLETON_MULTIPLE_TAGS,
      `Singleton range at ${singletonWithMultipleTags.rangeMin} can have either a min tag or a max tag, but not both`,
      { range: singletonWithMultipleTags, rangeIndex },
    );
  }

  const singletonValues = new Set<number>();
  const duplicateSingleton = singletonRanges.find((range) => {
    if (range.rangeMin === null) return false;
    if (singletonValues.has(range.rangeMin)) return true;
    singletonValues.add(range.rangeMin);
    return false;
  });

  if (duplicateSingleton) {
    const rangeIndex = ranges.indexOf(duplicateSingleton);
    return validationFailure(
      MetricRangeValidationErrorCode.METRIC_RANGE_MULTIPLE_SINGLETON,
      `Metric ranges must not include duplicate singleton ranges at ${duplicateSingleton.rangeMin}`,
      { range: duplicateSingleton, rangeIndex },
    );
  }

  return null;
}

function validateIdealCount(ranges: MetricRangeInput[]): MetricRangeValidationResult | null {
  const idealCount = ranges.filter((range) => range.ideal).length;
  if (idealCount === 0) {
    return validationFailure(
      MetricRangeValidationErrorCode.METRIC_RANGE_IDEAL_REQUIRED,
      "Metric ranges must include exactly one ideal range",
    );
  }

  if (idealCount > 1) {
    return validationFailure(
      MetricRangeValidationErrorCode.METRIC_RANGE_MULTIPLE_IDEAL,
      "Metric ranges must not include more than one ideal range",
    );
  }

  return null;
}

function validateIdealInclusivity(ranges: MetricRangeInput[]): MetricRangeValidationResult | null {
  const idealRange = ranges.find((range) => range.ideal);
  if (idealRange && (!idealRange.rangeMinInclusive || !idealRange.rangeMaxInclusive)) {
    const rangeIndex = ranges.indexOf(idealRange);
    return validationFailure(
      MetricRangeValidationErrorCode.METRIC_RANGE_IDEAL_NOT_INCLUSIVE,
      "The ideal metric range must include both boundaries",
      { range: idealRange, rangeIndex },
    );
  }

  return null;
}

function validateOuterCoverage(sorted: MetricRangeInput[]): MetricRangeValidationResult | null {
  const first = sorted[0];
  if (first.rangeMin !== null) {
    return validationFailure(
      MetricRangeValidationErrorCode.METRIC_RANGE_GAP,
      `Metric ranges must cover all values from -∞; first range starts at ${first.rangeMin}`,
      { range: first, rangeIndex: 0, sortedRanges: sorted },
    );
  }

  const last = sorted[sorted.length - 1];
  if (last.rangeMax !== null) {
    return validationFailure(
      MetricRangeValidationErrorCode.METRIC_RANGE_GAP,
      `Metric ranges must cover all values through ∞; last range ends at ${last.rangeMax}`,
      { range: last, rangeIndex: sorted.length - 1, sortedRanges: sorted },
    );
  }

  return null;
}

function validateAdjacentRanges(
  current: MetricRangeInput,
  next: MetricRangeInput,
  currentRangeIndex: number,
  sortedRanges: MetricRangeInput[],
): MetricRangeValidationResult | null {
  const currentMax = effectiveMax(current);
  const nextMin = effectiveMin(next);
  const adjacentRanges = {
    currentRange: current,
    currentRangeIndex,
    nextRange: next,
    nextRangeIndex: currentRangeIndex + 1,
  };

  if (currentMax > nextMin) {
    return validationFailure(
      MetricRangeValidationErrorCode.METRIC_RANGE_OVERLAP,
      `Ranges overlap: range ending at ${current.rangeMax ?? "∞"} overlaps with range starting at ${
        next.rangeMin ?? "-∞"
      }`,
      { adjacentRanges, sortedRanges },
    );
  }

  if (currentMax < nextMin) {
    return validationFailure(
      MetricRangeValidationErrorCode.METRIC_RANGE_GAP,
      `Gap between ranges: range ending at ${current.rangeMax ?? "∞"} ` +
        `does not reach range starting at ${next.rangeMin ?? "-∞"}`,
      { adjacentRanges, sortedRanges },
    );
  }

  if (current.rangeMax === null || next.rangeMin === null || current.rangeMax !== next.rangeMin) {
    return null;
  }

  if (current.rangeMaxInclusive && next.rangeMinInclusive) {
    return validationFailure(
      MetricRangeValidationErrorCode.METRIC_RANGE_BOTH_INCLUSIVE,
      `Shared boundary at ${current.rangeMax}: both ranges include this value`,
      { adjacentRanges, sortedRanges },
    );
  }

  if (!current.rangeMaxInclusive && !next.rangeMinInclusive) {
    return validationFailure(
      MetricRangeValidationErrorCode.METRIC_RANGE_NEITHER_INCLUSIVE,
      `Shared boundary at ${current.rangeMax}: neither range includes this value`,
      { adjacentRanges, sortedRanges },
    );
  }

  const currentMaxTagId =
    current.ideal && current.rangeMin === current.rangeMax
      ? current.maxTagId ?? current.minTagId ?? null
      : current.maxTagId ?? null;
  const nextMinTagId =
    next.ideal && next.rangeMin === next.rangeMax
      ? next.minTagId ?? next.maxTagId ?? null
      : next.minTagId ?? null;

  if (currentMaxTagId !== nextMinTagId) {
    return validationFailure(
      MetricRangeValidationErrorCode.METRIC_RANGE_TAG_MISMATCH,
      `Shared boundary at ${current.rangeMax}: max tag (${current.maxTagId ?? "none"}) ` +
        `does not match adjacent min tag (${next.minTagId ?? "none"})`,
      { adjacentRanges, sortedRanges },
    );
  }

  return null;
}

function validateInnerCoverage(sorted: MetricRangeInput[]): MetricRangeValidationResult | null {
  for (let i = 0; i < sorted.length - 1; i++) {
    const result = validateAdjacentRanges(sorted[i], sorted[i + 1], i, sorted);
    if (result) return result;
  }

  return null;
}

export function validateMetricRanges(ranges: MetricRangeInput[]): MetricRangeValidationResult {
  const rangeNamesError = validateRangeNames(ranges);
  if (rangeNamesError) return rangeNamesError;

  const rangeOrderError = validateRangeOrder(ranges);
  if (rangeOrderError) return rangeOrderError;

  const singletonRangesError = validateSingletonRanges(ranges);
  if (singletonRangesError) return singletonRangesError;

  const idealCountError = validateIdealCount(ranges);
  if (idealCountError) return idealCountError;

  const idealInclusivityError = validateIdealInclusivity(ranges);
  if (idealInclusivityError) return idealInclusivityError;

  const sorted = [...ranges].sort((a, b) => {
    const minDiff = effectiveMin(a) - effectiveMin(b);
    if (minDiff !== 0) return minDiff;
    return effectiveMax(a) - effectiveMax(b);
  });

  const outerCoverageError = validateOuterCoverage(sorted);
  if (outerCoverageError) return outerCoverageError;

  const innerCoverageError = validateInnerCoverage(sorted);
  if (innerCoverageError) return innerCoverageError;

  return { success: true, error: null };
}
